import { isNil, map, orderBy, uniqBy } from 'lodash';

import {
  ADMIN_HARD_SOFT_STOP_MESSAGES,
  ADMIN_SOFT_STOP_MESSAGES,
  arePreviousStagesCompleted,
  getProgressStepStatus,
  getStageMessage,
  isSoftHardStopStage,
  MEMBER_HARD_SOFT_STOP_MESSAGES,
  MEMBER_SOFT_STOP_MESSAGES,
  ProfileProgressStep,
  ProgressStepConfigParams,
  ProgressStepOptions,
  shouldDisplayRequestButton,
  shouldDisplayResponseButton,
  SOFT_HALF_STOP_MESSAGES,
} from './generalProgressUtils';
import {
  CustomerStageModel,
  CustomerStatusEnum,
  StageStatusEnum,
} from 'api/backofficeApiClient';
import { UserRole } from 'enums';

type CustomerProgressStep = ProfileProgressStep<CustomerStatusEnum>;
type CustomerProgressStepConfig = Partial<
  Record<
    CustomerStatusEnum,
    (step: ProgressStepConfigParams) => ProgressStepOptions
  >
>;

export const getCustomerProgressTrackerStageName = (
  stage: CustomerStatusEnum,
) => {
  switch (stage) {
    case CustomerStatusEnum.NewCustomerSetupStarted:
      return 'New customer setup started';
    case CustomerStatusEnum.CreditCheckVerified:
      return 'Credit check verified';
    case CustomerStatusEnum.UnderLegalReview:
      return 'Under legal review';
    case CustomerStatusEnum.AcquiringCertificateOfInsurance:
      return 'Acquiring certificate of insurance';
    case CustomerStatusEnum.ApprovedByFinance:
      return 'Approved by finance';
    case CustomerStatusEnum.ApprovedByLegal:
      return 'Approved by legal';
    case CustomerStatusEnum.CounterSignaturesCompleted:
      return 'Counter signatures completed';
    case CustomerStatusEnum.StaffOnboardingPackagesCreated:
      return 'Staff onboarding packages created';
    case CustomerStatusEnum.FinalAgreementUploaded:
      return 'Final agreement uploaded';
    case CustomerStatusEnum.NewCustomerSetupComplete:
      return 'New customer setup complete';
  }
};

const CUSTOMER_PROFILE_STAGES = [
  CustomerStatusEnum.NewCustomerSetupStarted,
  CustomerStatusEnum.CreditCheckVerified,
  CustomerStatusEnum.UnderLegalReview,
  CustomerStatusEnum.AcquiringCertificateOfInsurance,
  CustomerStatusEnum.ApprovedByFinance,
  CustomerStatusEnum.ApprovedByLegal,
  CustomerStatusEnum.CounterSignaturesCompleted,
  CustomerStatusEnum.StaffOnboardingPackagesCreated,
  CustomerStatusEnum.FinalAgreementUploaded,
  CustomerStatusEnum.NewCustomerSetupComplete,
];

const ADMIN_CLIENT_PROGRESS_STEPS_CONFIG = {
  [CustomerStatusEnum.NewCustomerSetupStarted]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.NewCustomerSetupStarted,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.CreditCheckVerified]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.CreditCheckVerified,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.UnderLegalReview]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.UnderLegalReview,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.AcquiringCertificateOfInsurance]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.AcquiringCertificateOfInsurance,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.ApprovedByFinance]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.ApprovedByFinance,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.ApprovedByLegal]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.ApprovedByLegal,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.CounterSignaturesCompleted]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.CounterSignaturesCompleted,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.StaffOnboardingPackagesCreated]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.StaffOnboardingPackagesCreated,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.FinalAgreementUploaded]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.FinalAgreementUploaded,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.NewCustomerSetupComplete]: ({
    arePrevStagesCompleted,
  }: ProgressStepConfigParams) => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.NewCustomerSetupComplete,
    ),
    isActionEnabled: arePrevStagesCompleted,
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
};

const CLIENT_PROGRESS_STEPS_CONFIG = {
  [CustomerStatusEnum.NewCustomerSetupStarted]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.NewCustomerSetupStarted,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.CreditCheckVerified]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.CreditCheckVerified,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.UnderLegalReview]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.UnderLegalReview,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.AcquiringCertificateOfInsurance]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.AcquiringCertificateOfInsurance,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.ApprovedByFinance]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.ApprovedByFinance,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.ApprovedByLegal]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.ApprovedByLegal,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.CounterSignaturesCompleted]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.CounterSignaturesCompleted,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.StaffOnboardingPackagesCreated]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.StaffOnboardingPackagesCreated,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.FinalAgreementUploaded]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.FinalAgreementUploaded,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [CustomerStatusEnum.NewCustomerSetupComplete]: () => ({
    title: getCustomerProgressTrackerStageName(
      CustomerStatusEnum.NewCustomerSetupComplete,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
};

// It's necessary for BE to send such stage, but it's not needed for Profile Progress Stages on FE
const removeUnusedStagesForProfileProgress = (stages: CustomerStageModel[]) => {
  return stages.filter(
    ({ stage }) => !isNil(stage) && stage !== CustomerStatusEnum.Requested,
  );
};

interface CustomerProgressStagesOptions {
  isAdmin?: boolean;
  stages?: CustomerStageModel[];
}
const getCustomerProgressSteps = (isAdmin?: boolean) => {
  return isAdmin
    ? ADMIN_CLIENT_PROGRESS_STEPS_CONFIG
    : CLIENT_PROGRESS_STEPS_CONFIG;
};

export const getCustomerProgressStages = (
  stagesOptions: CustomerProgressStagesOptions,
) => {
  const { stages, isAdmin } = stagesOptions;
  const customerStages = stages?.length
    ? removeUnusedStagesForProfileProgress(stages)
    : [];
  const customerStagesConfigByRole = getCustomerProgressSteps(
    isAdmin,
  ) as CustomerProgressStepConfig;

  const mergedProfileStages = map(
    [...customerStages, ...CUSTOMER_PROFILE_STAGES],
    (stage) => {
      return typeof stage === 'number'
        ? {
            stage,
            stageStatus: StageStatusEnum.Pending,
          }
        : stage;
    },
  );

  const sortedProfileStages = orderBy(
    mergedProfileStages,
    [(step) => step.stage, (step) => step.stageStatus],
    ['asc', 'desc'],
  );

  const uniqProfileStages = uniqBy(
    sortedProfileStages,
    (stageConfig) => stageConfig.stage,
  );

  const profileStagesConfigs = uniqProfileStages
    .map(
      (
        {
          showPulsatingIndicator,
          additionalInfoRequest,
          additionalInfoReply,
          ...step
        },
        stepIndex,
        profileStages,
      ) => {
        const getStageConfig =
          customerStagesConfigByRole[step.stage as CustomerStatusEnum];

        if (!getStageConfig) return undefined;

        const arePrevStagesCompleted = arePreviousStagesCompleted(
          profileStages,
          step,
        );

        const stageConfig = getStageConfig({ arePrevStagesCompleted });

        const {
          messages,
          responsibleRole,
          isActionEnabled: isActionEnabledFromConfig,
          displayRequestButton: displayRequestButtonFromConfig,
          displayResponseButton: displayResponseButtonFromConfig,
          ...rest
        } = stageConfig;

        const enableRequestButton = shouldDisplayRequestButton({
          isAdmin,
          arePrevStagesCompleted,
          responsibleRole,
          stageStatus: step.stageStatus,
        });

        const enableResponseButton = shouldDisplayResponseButton({
          isAdmin,
          requestMessage: additionalInfoRequest,
          responseMessage: additionalInfoReply,
        });

        const stepValidationStatus = getProgressStepStatus({
          step,
          stepIndex,
          profileStages,
        });

        const isPulsatingStage =
          isSoftHardStopStage(step.stageStatus) &&
          !isAdmin &&
          showPulsatingIndicator;

        return {
          ...rest,
          ...step,
          responsibleRole: responsibleRole || UserRole.Admin,
          message: getStageMessage(step.stageStatus as StageStatusEnum, {
            ...messages,
            isAdmin,
            arePrevStagesCompleted,
          }),
          isPulsating: isPulsatingStage,
          requestMessage: additionalInfoRequest,
          responseMessage: additionalInfoReply,
          displayResponseButton: isNil(displayResponseButtonFromConfig)
            ? enableResponseButton
            : displayResponseButtonFromConfig,
          displayRequestButton: isNil(displayRequestButtonFromConfig)
            ? enableRequestButton
            : displayRequestButtonFromConfig,
          isActionEnabled: isNil(isActionEnabledFromConfig)
            ? isAdmin
            : isActionEnabledFromConfig,
          stepValidationStatus,
        };
      },
    )
    .filter(Boolean) as CustomerProgressStep[];

  return profileStagesConfigs;
};
